import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { CatalogItemKind, CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem, CatalogSong, ReleaseKitSummary } from '../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { Cover } from '../../../../music24/components/primitives/Cover';
import { SMLPageHead } from '../../../../music24/components/SMLPageHead/SMLPageHead';
import { Breadcrumbs } from '../../../smlBreadcrumbs/types';
import { daysUntilRelease } from '../../../../music24/promote/releases/releaseOrder';
import { catalogArtworkSrc } from '../../catalogArtwork';
import { formatCatalogDay } from '../../catalogDates';
import { SongPageChips } from './SongPageChips';
import { SongPageMenu } from './SongPageMenu';
import { songPageMessages } from './songPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The release state line (design b1): "Unreleased · Out 4 Jun 2027 · in 241 days", or "Out 12 Sep 2026". */
export function songEyebrow(intl: IntlShape, item: CatalogItem, now: Date): string[] {
  if (item.kind !== CatalogItemKind.unreleased) {
    return item.releaseDate === null
      ? []
      : [intl.formatMessage(songPageMessages.eyebrowOut, { date: formatCatalogDay(intl, item.releaseDate) })];
  }
  const parts = [intl.formatMessage(songPageMessages.eyebrowUnreleased)];
  if (item.releaseDate === null) {
    parts.push(intl.formatMessage(songPageMessages.eyebrowNoDate));
    return parts;
  }
  parts.push(intl.formatMessage(songPageMessages.eyebrowOut, { date: formatCatalogDay(intl, item.releaseDate) }));
  const days = daysUntilRelease(item.releaseDate, now);
  if (days !== null && days > 0) {
    parts.push(intl.formatMessage(songPageMessages.eyebrowIn, { days }));
  }
  return parts;
}

function ownershipLabel(intl: IntlShape, item: CatalogItem): string {
  if (item.kind === CatalogItemKind.unreleased) {
    return intl.formatMessage(songPageMessages.addedByYou);
  }
  return intl.formatMessage(
    item.ownership === CatalogItemOwnership.featured ? songPageMessages.featured : songPageMessages.yours
  );
}

interface SongPageHeaderProps {
  item: CatalogItem;
  song: CatalogSong | undefined;
  kit: ReleaseKitSummary | undefined;
  breadcrumbs: Breadcrumbs;
  readOnly: boolean;
  onStudio: () => void;
  onReleases: () => void;
  onCopyLink: () => void;
  onEditLyrics: () => void;
  onRemove: () => void;
  onMerge?: () => void;
}

/**
 * The head of the song page (design b1): the crumbs, the cover, the release state, the title, the
 * artist and the type, the status chips, "Make in Studio", "Open in Releases" and the menu.
 */
const SongPageHeaderComponent: React.FC<SongPageHeaderProps> = ({
  item,
  song,
  kit,
  breadcrumbs,
  readOnly,
  onStudio,
  onReleases,
  onCopyLink,
  onEditLyrics,
  onRemove,
  onMerge,
}) => {
  const intl = useIntl();
  const eyebrow = useMemo(() => songEyebrow(intl, item, new Date()), [intl, item]);
  const sub = [item.artistName, item.releaseType, ownershipLabel(intl, item)]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');

  return (
    <SMLPageHead
      breadcrumbs={breadcrumbs}
      title={item.title}
      leading={<Cover src={catalogArtworkSrc(item, 112)} label={item.title} size={112} radius={14} />}
      meta={
        <>
          {eyebrow.length > 0 && (
            <div className={styles.eyebrow} data-testid="song-page-eyebrow">
              {eyebrow.join(' · ')}
            </div>
          )}
          <p className={styles.sub}>{sub}</p>
          <SongPageChips item={item} song={song} kit={kit} />
        </>
      }
    >
      <div className={styles.actions}>
        <SMLButton outline omitCap leftIcon={faSparkles} onClick={onStudio} disabled={readOnly}>
          {intl.formatMessage(songPageMessages.makeInStudio)}
        </SMLButton>
        <SMLButton color="primary" omitCap onClick={onReleases}>
          {intl.formatMessage(songPageMessages.openInReleases)}
        </SMLButton>
        <SongPageMenu
          isClaimed={item.kind !== CatalogItemKind.unreleased}
          canRemove={!readOnly}
          onCopyLink={onCopyLink}
          onEditLyrics={onEditLyrics}
          onRemove={onRemove}
          onMerge={readOnly ? undefined : onMerge}
        />
      </div>
    </SMLPageHead>
  );
};

export const SongPageHeader = TypedMemo(SongPageHeaderComponent);
